import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, FlaskConical, FileText, Shield, Minus, Plus, FileCheck, CheckCircle2, Eye, Truck, Award, Package, ChevronLeft, ChevronRight, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { productAPI, coaAPI } from '../lib/api';
import { useCart } from '../context/CartContext';
import { useRegional } from '../context/RegionalContext';
import { toast } from 'sonner';
import COAModal from '../components/COAModal';
import SEOHead, { generateProductSchema, generateSlug } from '../components/SEO/SEOHead';
import { getProductSEO } from '../components/SEO/seoData';
import { ProductTrustSection } from '../components/SEO/TrustBadges';
import { trackViewContent } from '../utils/tracking';

// ============================================================
// RELATED PRODUCT CARD - Compact conversion-focused card
// ============================================================
const RelatedProductCard = ({ product, onAddToCart, region }) => {
  const [isAdding, setIsAdding] = useState(false);
  const currencySymbol = region?.symbol || '$';

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsAdding(true);
    try {
      await onAddToCart(product.id, 1, product);
      toast.success(`${product.name} ${'added to cart'}`);
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <Link
      to={`/products/${product.slug || product.id}`}
      className="group flex-shrink-0 w-64 bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all"
    >
      <div className="aspect-square bg-gradient-to-br from-slate-50 to-white p-4">
        <img
          src={product.image_url}
          alt={product.name}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
        />
      </div>
      <div className="p-4">
        <h4 className="font-semibold text-sm text-slate-900 group-hover:text-blue-600 transition-colors line-clamp-1">
          {product.name}
        </h4>
        <p className="text-xs text-slate-500 mt-0.5">{product.quantity || product.dosage}</p>
        <div className="flex items-center justify-between mt-3">
          <span className="font-bold text-slate-900">{currencySymbol}{product.price?.toFixed(2)}</span>
          <button
            onClick={handleQuickAdd}
            disabled={isAdding || !product.in_stock}
            className="p-2 bg-slate-900 text-white rounded-full hover:bg-slate-800 disabled:opacity-50 transition-colors"
          >
            <ShoppingCart className="w-4 h-4" />
          </button>
        </div>
      </div>
    </Link>
  );
};

// ============================================================
// THUMBNAIL GALLERY - Product image thumbnails
// ============================================================
const ThumbnailGallery = ({ images, activeIndex, onSelect }) => (
  <div className="flex gap-2 mt-4">
    {images.map((img, idx) => (
      <button
        key={idx}
        onClick={() => onSelect(idx)}
        className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
          activeIndex === idx ? 'border-blue-500' : 'border-slate-200 hover:border-slate-300'
        }`}
      >
        <img src={img.url} alt={img.label} className="w-full h-full object-contain bg-slate-50 p-1" />
      </button>
    ))}
  </div>
);

// ============================================================
// SPECIFICATIONS CARD
// ============================================================
const SpecificationsCard = ({ product }) => {
  const specs = [
    { label: 'Compound', value: product.name },
    { label: 'Form', value: 'Lyophilized powder' },
    { label: 'Size', value: product.dosage },
    { label: 'Assay Standard', value: product.purity || 'Analytical Grade' },
    { label: 'Testing', value: 'Third-party verified' },
    { label: 'Documentation', value: 'TDS available' },
    { label: 'Batch', value: product.batch_number || 'Current production' },
  ];

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <Info className="w-4 h-4 text-blue-500" />
        {'Specifications'}
      </h3>
      <dl className="space-y-3">
        {specs.map((spec, idx) => (
          <div key={idx} className="flex justify-between text-sm">
            <dt className="text-slate-500">{spec.label}</dt>
            <dd className="font-medium text-slate-900 text-right">{spec.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

// ============================================================
// MAIN PRODUCT DETAIL PAGE
// ============================================================
const ProductDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart, loading: cartLoading } = useCart();
  const { region, currency, getProductPrice } = useRegional();
  const viewContentTrackedRef = useRef(false);
  
  const [product, setProduct] = useState(null);
  const [productCOAs, setProductCOAs] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [selectedCOA, setSelectedCOA] = useState(null);
  const [coaModalOpen, setCOAModalOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  
  // Regional pricing
  const [regionalPrice, setRegionalPrice] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(false);

  // Auto-select the first variant when product loads
  useEffect(() => {
    if (product?.has_variants && product?.variants?.length > 0 && !selectedVariant) {
      // Default to 10mg if available, otherwise first variant
      const default10mg = product.variants.find(v => v.mg === 10);
      setSelectedVariant(default10mg || product.variants[0]);
    }
  }, [product, selectedVariant]);

  // Effective price/sku/size based on variant selection
  const effectivePrice = selectedVariant?.price ?? product?.price ?? 0;
  const effectiveSize = selectedVariant?.size ?? product?.dosage ?? '';
  const effectiveSku = selectedVariant?.sku ?? product?.sku ?? '';
  const effectiveInStock = selectedVariant ? selectedVariant.in_stock : (product?.in_stock ?? true);

  // Helper to translate category names
  const translateCategory = (category) => {
    const categoryMap = {
      'Analytical Reagents': 'Analytical Reagents',
      'Research Blends': 'Blends',
      'Single Amino Acid Derivatives': 'Single Amino Acid Derivatives',
    };
    return categoryMap[category] || category;
  };
  
  // Fetch regional price when product changes
  useEffect(() => {
    const fetchRegionalPrice = async () => {
      if (product && product.id) {
        setLoadingPrice(true);
        try {
          const priceInfo = await getProductPrice(product.id, product.price);
          setRegionalPrice(priceInfo);
        } catch (error) {
          console.warn('Failed to get regional price:', error);
        }
        setLoadingPrice(false);
      }
    };
    
    fetchRegionalPrice();
  }, [product, region.code, getProductPrice]);

  // Track ViewContent when product loads
  useEffect(() => {
    if (product && !viewContentTrackedRef.current) {
      viewContentTrackedRef.current = true;
      trackViewContent({
        id: product.id,
        slug: product.slug || product.id,
        name: product.name,
        price: product.price,
        currency: currency || 'USD',
        category: product.category
      }, region?.code || 'US');
      console.log('[Tracking] ViewContent fired for product:', product.name);
    }
  }, [product, region, currency]);

  // Reset tracking ref when product slug changes
  useEffect(() => {
    viewContentTrackedRef.current = false;
  }, [slug]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch product by slug (or ID for backward compatibility)
        const productRes = await productAPI.getOne(slug);
        const productData = productRes.data;
        const productId = productData.id;
        
        const [coaRes, allProductsRes] = await Promise.all([
          coaAPI.getByProduct(productId),
          productAPI.getAll()
        ]);
        
        setProduct(productData);
        setProductCOAs(coaRes.data);
        
        // Get related products (same category, excluding current)
        const related = allProductsRes.data
          .filter(p => p.id !== productId)
          .slice(0, 6);
        setRelatedProducts(related);
      } catch (error) {
        console.error('Failed to fetch product:', error);
        toast.error('Product not found');
        navigate('/products');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    window.scrollTo(0, 0);
  }, [slug, navigate]);

  const handleAddToCart = async () => {
    try {
      setIsAdding(true);
      // For variant-enabled products, attach the selected variant's SKU, size, and price
      const variantMeta = selectedVariant ? {
        variant_sku: selectedVariant.sku,
        variant_size: selectedVariant.size,
        variant_price: selectedVariant.price,
        variant_mg: selectedVariant.mg,
      } : {};
      const productWithVariant = { ...product, ...variantMeta };
      await addToCart(product.id, quantity, productWithVariant);
      const label = selectedVariant ? `${product.name} (${selectedVariant.size})` : product.name;
      toast.success(`${label} added to cart`);
    } catch (error) {
      toast.error('Failed to add item to cart');
    } finally {
      setIsAdding(false);
    }
  };

  const handleViewCOA = (coa) => {
    setSelectedCOA(coa);
    setCOAModalOpen(true);
  };

  const openProductCOA = () => {
    const coaData = productCOAs[0] || {
      product_name: product.name,
      product_category: product.category,
      batch_number: product.batch_number || 'Current Batch',
      purity_percentage: product.purity?.replace(/[^0-9.]/g, '') || '99',
      test_date: new Date().toISOString(),
      lab_name: 'Analytical Facility',
      coa_url: product.coa_url,
      pdf_url: product.coa_url
    };
    setSelectedCOA(coaData);
    setCOAModalOpen(true);
  };

  // Generate image gallery
  const getImageGallery = () => {
    const images = [
      { url: product.image_url, label: 'Product' },
    ];
    // Only add TDS as separate thumbnail if there's a different TDS image/PDF preview
    // Don't duplicate the product image
    return images;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-slate-200 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) return null;

  const imageGallery = getImageGallery();
  
  // Generate SEO data for this product
  const seoData = getProductSEO(product);
  const productSchema = generateProductSchema(product);
  const productSlug = generateSlug(product.name);
  
  // Breadcrumbs for schema
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Products', url: '/products' },
    { name: product.name, url: `/products/${productSlug}` },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="product-detail-page">
      {/* SEO Head with Product Schema - noindex to prevent consumer search results */}
      <SEOHead
        title={seoData.title}
        description={seoData.description}
        canonical={`/products/${productSlug}`}
        ogType="product"
        ogImage={product.image_url}
        keywords={seoData.keywords}
        structuredData={productSchema}
        breadcrumbs={breadcrumbs}
        noindex={true}
      />
      
      {/* Breadcrumb - No padding needed, content starts right after header */}
      <div className="bg-[#fafafa] py-1.5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/products')}
            className="flex items-center text-sm text-slate-600 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            {'Back to Products'}
          </button>
        </div>
      </div>

      {/* ===== ABOVE THE FOLD - 2 Column Layout ===== */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-3 lg:py-6">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          
          {/* LEFT COLUMN - Product Image */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="aspect-square bg-gradient-to-br from-slate-50 via-white to-slate-50 rounded-2xl overflow-hidden border border-slate-200">
              <img
                src={imageGallery[activeImageIndex]?.url || product.image_url}
                alt={product.name}
                className="w-full h-full object-contain p-8 lg:p-12"
                data-testid="product-image"
              />
            </div>
            
            {/* Thumbnail Gallery */}
            {imageGallery.length > 1 && (
              <ThumbnailGallery
                images={imageGallery}
                activeIndex={activeImageIndex}
                onSelect={setActiveImageIndex}
              />
            )}
          </div>

          {/* RIGHT COLUMN - Product Info & CTA */}
          <div className="flex flex-col">
            {/* Category Badge */}
            <Badge className="w-fit bg-slate-100 text-slate-700 border-0 mb-3">
              {translateCategory(product.category)}
            </Badge>
            
            {/* Product Name (H1) */}
            <h1 className="font-heading font-bold text-3xl lg:text-4xl text-slate-900 mb-2" data-testid="product-name">
              {product.name}
            </h1>
            
            {/* Subtitle */}
            <p className="text-slate-500 mb-4">
              {'Lyophilized amino acid derivative'} • {product.has_variants ? effectiveSize : product.dosage} • {'Research use only'}
            </p>
            
            {/* Price */}
            <div className="flex items-baseline gap-2 mb-6">
              <span className="font-heading font-bold text-3xl text-slate-900" data-testid="product-price">
                {product.has_variants ? (
                  `$${effectivePrice.toFixed(2)}`
                ) : regionalPrice ? (
                  `${regionalPrice.currency_symbol || region.symbol}${regionalPrice.price?.toFixed(2)}`
                ) : (
                  `$${product.price?.toFixed(2)}`
                )}
              </span>
              <span className="text-sm text-slate-500">{'per container'}</span>
              {region.code !== 'US' && regionalPrice && !product.has_variants && (
                <span className="text-xs text-slate-400">({region.currency})</span>
              )}
            </div>

            {/* Size Variant Selector — B2B bulk sizes */}
            {product.has_variants && product.variants?.length > 0 && (
              <div className="mb-6" data-testid="variant-selector">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-slate-700">Size</span>
                  {selectedVariant && (
                    <span className="text-xs text-slate-500 font-mono">SKU: {selectedVariant.sku}</span>
                  )}
                </div>
                <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
                  {product.variants.map((v) => {
                    const active = selectedVariant?.sku === v.sku;
                    return (
                      <button
                        key={v.sku}
                        onClick={() => setSelectedVariant(v)}
                        disabled={!v.in_stock}
                        className={`
                          px-2 py-2.5 rounded-lg border text-center transition-all
                          ${active
                            ? 'border-slate-900 bg-slate-900 text-white shadow-sm'
                            : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'}
                          ${!v.in_stock ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                        data-testid={`variant-${v.size}`}
                      >
                        <div className="font-semibold text-sm">{v.size}</div>
                        <div className={`text-xs mt-0.5 ${active ? 'text-slate-300' : 'text-slate-500'}`}>
                          ${v.price >= 1000 ? (v.price / 1000).toFixed(1) + 'k' : v.price.toFixed(0)}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  Bulk institutional pricing — larger sizes carry per-mg discounts. All sizes ship with TDS.
                </p>
              </div>
            )}

            {/* Quantity Selector */}
            <div className="flex items-center gap-4 mb-4">
              <span className="text-sm font-medium text-slate-700">{'Quantity'}:</span>
              <div className="flex items-center border border-slate-200 rounded-full">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 flex items-center justify-center text-slate-600 hover:text-slate-900 transition-colors"
                  data-testid="decrease-quantity"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-10 text-center font-semibold text-slate-900" data-testid="quantity-value">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 flex items-center justify-center text-slate-600 hover:text-slate-900 transition-colors"
                  data-testid="increase-quantity"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Primary CTA - Add to Cart */}
            <Button
              onClick={handleAddToCart}
              disabled={!effectiveInStock || isAdding || cartLoading}
              className="w-full h-14 bg-slate-900 hover:bg-slate-800 text-white rounded-full text-base font-semibold transition-all mb-3"
              data-testid="add-to-cart-btn"
            >
              <ShoppingCart className="mr-2 h-5 w-5" />
              {!effectiveInStock ? 'Out of Stock' : isAdding ? 'Adding...' : `Add ${effectiveSize} to Cart`}
            </Button>

            {/* Secondary CTA - View TDS */}
            <Button
              onClick={openProductCOA}
              variant="outline"
              className="w-full h-12 rounded-full text-sm font-medium border-slate-300 hover:bg-slate-50"
              data-testid="view-coa-btn"
            >
              <FileCheck className="mr-2 h-4 w-4" />
              {'View Technical Data Sheet'}
            </Button>

            {/* Research Link - Internal Linking for SEO */}
            <Link
              to={`/portal/amino acid derivative/${product.research_slug || product.slug || generateSlug(product.name)}`}
              className="w-full h-12 rounded-full text-sm font-medium border border-sky-200 bg-sky-50 hover:bg-sky-100 text-sky-700 flex items-center justify-center gap-2 mt-2 transition-colors"
              data-testid="view-research-btn"
            >
              <FlaskConical className="h-4 w-4" />
              {'View Research Profile'}
            </Link>

            {/* Trust Line */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-4 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                {'cGMP manufactured'}
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                {'Third-party tested'}
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                {'Batch TDS available'}
              </span>
            </div>

            {/* Compliance Strip */}
            <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-100">
              <p className="text-xs text-slate-500 flex items-center gap-2">
                <Shield className="w-4 h-4 text-slate-400" />
                {'For Institutional Laboratory Use Only — Not for consumer use.'}
              </p>
            </div>

            {/* Specifications Card (Desktop) */}
            <div className="hidden lg:block mt-6">
              <SpecificationsCard product={product} />
            </div>
          </div>
        </div>
      </section>

      {/* Specifications Card (Mobile) */}
      <section className="lg:hidden container mx-auto px-4 sm:px-6 pb-8">
        <SpecificationsCard product={product} />
      </section>

      {/* ===== PRODUCT INFO TABS ===== */}
      <section className="border-t border-slate-100 bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          <Tabs defaultValue="description" className="w-full">
            <TabsList className="w-full flex justify-start bg-white border border-slate-200 rounded-xl p-1 h-auto overflow-x-auto">
              <TabsTrigger
                value="description"
                className="flex-shrink-0 rounded-lg px-4 py-2.5 text-sm font-medium data-[state=active]:bg-slate-900 data-[state=active]:text-white"
              >
                {'Description'}
              </TabsTrigger>
              <TabsTrigger
                value="storage"
                className="flex-shrink-0 rounded-lg px-4 py-2.5 text-sm font-medium data-[state=active]:bg-slate-900 data-[state=active]:text-white"
              >
                {'Storage & Handling'}
              </TabsTrigger>
              <TabsTrigger
                value="coa"
                className="flex-shrink-0 rounded-lg px-4 py-2.5 text-sm font-medium data-[state=active]:bg-slate-900 data-[state=active]:text-white"
                data-testid="coa-tab"
              >
                {'TDS'}
              </TabsTrigger>
              <TabsTrigger
                value="disclaimer"
                className="flex-shrink-0 rounded-lg px-4 py-2.5 text-sm font-medium data-[state=active]:bg-slate-900 data-[state=active]:text-white"
              >
                {'Disclaimer'}
              </TabsTrigger>
            </TabsList>
            
            {/* Description Tab */}
            <TabsContent value="description" className="mt-6">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-lg text-slate-900 mb-4">{'About'} {product.name}</h3>
                <p className="text-slate-600 leading-relaxed mb-6">
                  {product.description}
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">{'Form'}</p>
                    <p className="font-medium text-slate-900">{'Lyophilized powder'}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">{'Assay Standard'}</p>
                    <p className="font-medium text-slate-900">{product.purity || 'Analytical Grade'}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">{'Quantity'}</p>
                    <p className="font-medium text-slate-900">{product.dosage}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">{'Intended Use'}</p>
                    <p className="font-medium text-slate-900">{'Laboratory research'}</p>
                  </div>
                </div>
              </div>
            </TabsContent>
            
            {/* Storage Tab */}
            <TabsContent value="storage" className="mt-6">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-lg text-slate-900 mb-4">{'Storage & Handling'}</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Package className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{'Storage Conditions'}</p>
                      <p className="text-sm text-slate-600 mt-1">{product.storage || 'Store at -20°C for long-term storage. Protect from light and moisture.'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FlaskConical className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{'Handling Guidelines'}</p>
                      <ul className="text-sm text-slate-600 mt-1 space-y-1">
                        <li>• {'Store in original packaging until ready for use'}</li>
                        <li>• {'Avoid repeated freeze-thaw cycles'}</li>
                        <li>• {'Use appropriate laboratory safety equipment'}</li>
                        <li>• {'Keep away from direct light and moisture'}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
            
            {/* TDS Tab */}
            <TabsContent value="coa" className="mt-6" data-testid="coa-tab-content">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
                  <FileCheck className="w-5 h-5 text-blue-600" />
                  {'Technical Data Sheet'}
                </h3>
                
                {productCOAs.length > 0 ? (
                  <div className="space-y-4">
                    <p className="text-slate-600 text-sm">
                      {'Select a batch to view the corresponding Technical Data Sheet.'}
                    </p>
                    
                    {/* TDS List */}
                    {productCOAs.map((coa) => (
                      <div key={coa.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-slate-900">{'Batch'}: {coa.batch_number}</p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {'Tested'}: {new Date(coa.test_date).toLocaleDateString()} • {coa.lab_name}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold text-green-600">{coa.purity_percentage}%</span>
                          <Button
                            onClick={() => handleViewCOA(coa)}
                            size="sm"
                            className="bg-slate-900 hover:bg-slate-800"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            {'View'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : product.coa_url ? (
                  <div className="space-y-4">
                    <p className="text-slate-600 text-sm">
                      {'Technical Data Sheet available for this product.'}
                    </p>
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-900">{'Batch'}: {product.batch_number || 'Current'}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{'Third-party verified'}</p>
                      </div>
                      <Button
                        onClick={openProductCOA}
                        size="sm"
                        className="bg-slate-900 hover:bg-slate-800"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        {'View TDS'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 bg-slate-50 rounded-lg">
                    <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="font-medium text-slate-700 mb-1">{'TDS Coming Soon'}</p>
                    <p className="text-sm text-slate-500">
                      {'Technical Data Sheet will be available shortly.'}
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>
            
            {/* Disclaimer Tab */}
            <TabsContent value="disclaimer" className="mt-6">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-lg text-slate-900 mb-4">{'Institutional Laboratory Use Only'}</h3>
                <div className="space-y-4 text-sm text-slate-600">
                  <p>
                    {'This product is intended for laboratory institutional laboratory use only. It is not intended for consumer or veterinary use, for use in food or cosmetic products, or for any institutional, diagnostic, or medicinal purposes.'}
                  </p>
                  <p>
                    {'The purchaser assumes all responsibility for ensuring that the product is handled, stored, and used in compliance with all applicable laws and regulations.'}
                  </p>
                  <p>
                    {'Amidex makes no claims regarding the safety or efficacy of this product for any specific purpose. Product specifications are provided for research reference only.'}
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* ===== TRUST & GUARANTEE SECTION ===== */}
      <section className="bg-slate-50 border-t border-slate-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-xl mx-auto">
            <ProductTrustSection />
          </div>
        </div>
      </section>

      {/* ===== DOCUMENTATION & VERIFICATION SECTION ===== */}
      <section className="bg-white border-t border-slate-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading font-bold text-2xl text-slate-900 mb-4">
              {'Documentation & Verification'}
            </h2>
            <p className="text-slate-600 mb-8">
              {'Every product ships with complete documentation for research verification.'}
            </p>
            
            <div className="grid sm:grid-cols-3 gap-6 mb-8">
              <div className="flex flex-col items-center p-4">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-3">
                  <FileCheck className="w-6 h-6 text-blue-600" />
                </div>
                <p className="font-medium text-slate-900">{'Batch-specific TDS'}</p>
                <p className="text-xs text-slate-500 mt-1">{'Unique to each production batch'}</p>
              </div>
              <div className="flex flex-col items-center p-4">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-3">
                  <FlaskConical className="w-6 h-6 text-blue-600" />
                </div>
                <p className="font-medium text-slate-900">{'Third-party lab analysis'}</p>
                <p className="text-xs text-slate-500 mt-1">{'Independent verification'}</p>
              </div>
              <div className="flex flex-col items-center p-4">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-3">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <p className="font-medium text-slate-900">{'Transparent documentation'}</p>
                <p className="text-xs text-slate-500 mt-1">{'Full traceability'}</p>
              </div>
            </div>
            
            <Button
              onClick={openProductCOA}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8"
            >
              <Eye className="w-4 h-4 mr-2" />
              {'View TDS'}
            </Button>
          </div>
        </div>
      </section>

      {/* ===== RELATED PRODUCTS CAROUSEL ===== */}
      {relatedProducts.length > 0 && (
        <section className="bg-slate-50 border-t border-slate-100">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-xl text-slate-900">
                {'Related Products'}
              </h2>
              <Link 
                to="/products" 
                className="text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                {'View All'} →
              </Link>
            </div>
            
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
              {relatedProducts.map((relatedProduct) => (
                <RelatedProductCard
                  key={relatedProduct.id}
                  product={relatedProduct}
                  onAddToCart={addToCart}
                  region={region}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== MOBILE STICKY ADD TO CART ===== */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 z-50">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <p className="font-bold text-lg text-slate-900">
              {product.has_variants ? (
                `$${effectivePrice.toFixed(2)}`
              ) : regionalPrice ? (
                `${regionalPrice.currency_symbol || region.symbol}${regionalPrice.price?.toFixed(2)}`
              ) : (
                `$${product.price?.toFixed(2)}`
              )}
            </p>
            <p className="text-xs text-slate-500">
              {product.name}{product.has_variants && effectiveSize ? ` • ${effectiveSize}` : ''}
            </p>
          </div>
          <Button
            onClick={handleAddToCart}
            disabled={!effectiveInStock || isAdding || cartLoading}
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6 h-12"
          >
            <ShoppingCart className="mr-2 h-4 w-4" />
            {isAdding ? 'Adding...' : 'Add to Cart'}
          </Button>
        </div>
      </div>

      {/* Add bottom padding on mobile for sticky bar */}
      <div className="lg:hidden h-24" />

      {/* TDS Modal */}
      <COAModal 
        isOpen={coaModalOpen} 
        onClose={() => setCOAModalOpen(false)} 
        coa={selectedCOA}
        product={product}
      />
    </div>
  );
};

export default ProductDetailPage;
